/**
 * Product Customization Form Handler
 * Gestiona la personalización de productos usando Product Bundling de Shopify
 * Basado en el sistema de valencia-basket-horizon adaptado para PadelProShop
 */

class ProductCustomizationFormHandler {
  constructor() {
    this.form = null;
    this.variantIdInput = null;
    this.parentIdInputs = [];
    this.addonInputsByIndex = new Map();
    this.togglesByIndex = new Map();
    this.textInputsByIndex = new Map();
    this.pidsByIndex = new Map(); // Guardar PIDs generados

    this.init();
  }

  /**
   * Inicializa el handler
   */
  init() {
    // Esperar a que el DOM esté listo
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Configura todos los elementos y event listeners
   */
  setup() {
    // Encontrar el formulario principal del producto
    this.form = document.querySelector(".js-product-form");
    if (!this.form) {
      return;
    }

    this.isBundleProduct = this.form.dataset.isBundle === "true";

    // Encontrar el input del variant ID principal
    this.variantIdInput = this.form.querySelector('input[name="id"]');
    if (!this.variantIdInput) {
      return;
    }

    // IMPORTANTE: Crear los inputs dinámicamente DENTRO del formulario
    this.createAddonInputs();

    // Recopilar todos los toggles (checkboxes)
    const toggles = document.querySelectorAll(".personalization-toggle");
    toggles.forEach((toggle) => {
      const addonIndex = toggle.dataset.addonIndex;
      this.togglesByIndex.set(addonIndex, toggle);

      // Event listener para el toggle
      toggle.addEventListener("change", (e) => this.handleToggleChange(e));
    });

    // Recopilar inputs de texto
    const textInputs = document.querySelectorAll(".customization-text-input");
    textInputs.forEach((input) => {
      const addonIndex = input.dataset.addonIndex;
      this.textInputsByIndex.set(addonIndex, input);

      // Event listener para sincronizar el texto con el property hidden
      input.addEventListener("input", (e) => this.handleTextInputChange(e));
    });

    // Sincronizar parent_id inicial
    this.updateAllParentIds();

    // Listener para cambios de variante
    this.variantIdInput.addEventListener("change", () =>
      this.updateAllParentIds(),
    );

    // También escuchar el evento custom de cambio de variante (si existe)
    document.addEventListener("variant:change", () =>
      this.updateAllParentIds(),
    );
  }

  /**
   * Genera un PID único
   */
  generatePid() {
    return Math.floor(
      Math.random() * Math.floor(Number.MAX_SAFE_INTEGER),
    ).toString();
  }

  /**
   * Crea los inputs de addon dinámicamente DENTRO del formulario
   */
  createAddonInputs() {
    // Buscar todos los contenedores de datos de addon
    const addonDataContainers = document.querySelectorAll(
      ".customization-addon-data",
    );

    if (addonDataContainers.length === 0) {
      return;
    }

    let mainProductInputsCreated = false;

    addonDataContainers.forEach((container) => {
      const addonIndex = container.dataset.addonIndex;
      const addonVariantId = container.dataset.addonVariantId;
      const currentVariantId = container.dataset.currentVariantId;
      const addonName = container.dataset.addonName;

      // ========== CREAR INPUTS DEL PRODUCTO PRINCIPAL (items[0]) - UNA SOLA VEZ ==========
      if (!mainProductInputsCreated) {
        // NO crear un nuevo input de ID, usaremos el del liquid con name="id"
        // Solo crear los inputs de properties del producto principal

        // Input: items[0][quantity]
        const mainQuantityInput = document.createElement("input");
        mainQuantityInput.type = "hidden";
        mainQuantityInput.name = "";
        mainQuantityInput.value = "1";
        mainQuantityInput.className =
          "customization-addon-input customization-main-quantity";
        mainQuantityInput.dataset.name = "items[0]quantity";
        this.form.appendChild(mainQuantityInput);

        // Input: items[0][properties][_pid]
        const mainPidInput = document.createElement("input");
        mainPidInput.type = "hidden";
        mainPidInput.name = "";
        mainPidInput.value = "";
        mainPidInput.className =
          "customization-addon-input customization-main-pid";
        mainPidInput.dataset.name = "items[0]properties[_pid]";
        this.form.appendChild(mainPidInput);

        // Input: items[0][properties][customized]
        const mainCustomizedInput = document.createElement("input");
        mainCustomizedInput.type = "hidden";
        mainCustomizedInput.name = "";
        mainCustomizedInput.value = "true";
        mainCustomizedInput.className =
          "customization-addon-input customization-main-customized";
        mainCustomizedInput.dataset.name = "items[0]properties[_customized]";
        this.form.appendChild(mainCustomizedInput);

        // Input: items[0][properties][_linkedItem] - Al habilitar primera personalización, se setea el ID del addon
        const mainLinkedItemInput = document.createElement("input");
        mainLinkedItemInput.type = "hidden";
        mainLinkedItemInput.name = "";
        mainLinkedItemInput.value = "";
        mainLinkedItemInput.className =
          "customization-addon-input customization-main-linked-item";
        mainLinkedItemInput.dataset.name = "items[0]properties[_linkedItem]";
        this.form.appendChild(mainLinkedItemInput);

        mainProductInputsCreated = true;
      }

      // ========== CREAR INPUTS DEL ADDON (items[N][...]) ==========
      const inputs = [];

      // Input: items[N][id]
      const idInput = document.createElement("input");
      idInput.type = "hidden";
      idInput.name = "";
      idInput.value = addonVariantId;
      idInput.className = "customization-addon-input";
      idInput.dataset.addonIndex = addonIndex;
      idInput.dataset.name = `items[${addonIndex}]id`;
      this.form.appendChild(idInput);
      inputs.push(idInput);

      // Input: items[N][quantity]
      const quantityInput = document.createElement("input");
      quantityInput.type = "hidden";
      quantityInput.name = "";
      quantityInput.value = "1";
      quantityInput.className = "customization-addon-input";
      quantityInput.dataset.addonIndex = addonIndex;
      quantityInput.dataset.name = `items[${addonIndex}]quantity`;
      this.form.appendChild(quantityInput);
      inputs.push(quantityInput);

      // Input: items[N][parent_id]
      const parentIdInput = document.createElement("input");
      parentIdInput.type = "hidden";
      parentIdInput.name = "";
      parentIdInput.value = currentVariantId;
      parentIdInput.className =
        "customization-addon-input customization-parent-id";
      parentIdInput.dataset.addonIndex = addonIndex;
      if (!this.isBundleProduct)
        parentIdInput.dataset.name = `items[${addonIndex}]parent_id`;
      this.form.appendChild(parentIdInput);
      inputs.push(parentIdInput);
      this.parentIdInputs.push(parentIdInput);

      // Input: items[N][properties][Nombre]
      if (addonName) {
        const propertyInput = document.createElement("input");
        propertyInput.type = "hidden";
        propertyInput.name = "";
        propertyInput.value = "";
        propertyInput.className =
          "customization-addon-input customization-addon-property";
        propertyInput.dataset.addonIndex = addonIndex;
        propertyInput.dataset.propertyName = addonName;
        propertyInput.dataset.name = `items[${addonIndex}]properties[${addonName}]`;
        this.form.appendChild(propertyInput);
        inputs.push(propertyInput);
      }

      // Input: items[N][properties][_pid]
      const pidInput = document.createElement("input");
      pidInput.type = "hidden";
      pidInput.name = "";
      pidInput.value = "";
      pidInput.className = "customization-addon-input customization-addon-pid";
      pidInput.dataset.addonIndex = addonIndex;
      pidInput.dataset.name = `items[${addonIndex}]properties[_pid]`;
      this.form.appendChild(pidInput);
      inputs.push(pidInput);

      // Input: items[N][properties][customized]
      const addonCustomizedInput = document.createElement("input");
      addonCustomizedInput.type = "hidden";
      addonCustomizedInput.name = "";
      addonCustomizedInput.value = "true";
      addonCustomizedInput.className =
        "customization-addon-input customization-addon-customized";
      addonCustomizedInput.dataset.addonIndex = addonIndex;
      addonCustomizedInput.dataset.name = `items[${addonIndex}]properties[_customized]`;
      this.form.appendChild(addonCustomizedInput);
      inputs.push(addonCustomizedInput);

      // Input: items[N][properties][_hidden] - Para ocultar en el carrito
      const addonHiddenInput = document.createElement("input");
      addonHiddenInput.type = "hidden";
      addonHiddenInput.name = "";
      addonHiddenInput.value = "true";
      addonHiddenInput.className =
        "customization-addon-input customization-addon-hidden";
      addonHiddenInput.dataset.addonIndex = addonIndex;
      addonHiddenInput.dataset.name = `items[${addonIndex}]properties[_hidden]`;
      this.form.appendChild(addonHiddenInput);
      inputs.push(addonHiddenInput);

      // Guardar todos los inputs de este addon
      this.addonInputsByIndex.set(addonIndex, inputs);
    });
  }

  /**
   * Maneja el cambio del checkbox de personalización
   */
  handleToggleChange(event) {
    const toggle = event.target;
    const addonIndex = toggle.dataset.addonIndex;
    const isChecked = toggle.checked;

    if (isChecked) {
      this.enableAddonInputs(addonIndex);
      this.showTextInput(addonIndex);
      this.convertMainProductToArray(); // Convertir a formato de array
    } else {
      this.disableAddonInputs(addonIndex);
      this.hideTextInput(addonIndex);

      // Verificar si quedan addons activos
      const hasActiveAddons = Array.from(this.togglesByIndex.values()).some(
        (t) => t.checked,
      );
      if (!hasActiveAddons) {
        this.revertMainProductToNormal(); // Revertir a formato normal
      }
    }
  }

  /**
   * Convierte los inputs principales a formato de array items[0][...]
   */
  convertMainProductToArray() {
    // Cambiar el input id original del liquid a items[0]id
    if (this.variantIdInput && this.variantIdInput.name === "id") {
      this.variantIdInput.name = "items[0]id";
      // Habilitar el input para que se incluya en FormData
      this.variantIdInput.disabled = false;
    }

    // Cambiar el input quantity a items[0]quantity si existe
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    if (quantityInput && quantityInput.name === "quantity") {
      quantityInput.name = "items[0]quantity";
      quantityInput.disabled = false;
    }

    // Habilitar los inputs dinámicos de properties del producto principal
    const mainQuantityInput = this.form.querySelector(
      ".customization-main-quantity",
    );
    if (mainQuantityInput && !mainQuantityInput.name) {
      mainQuantityInput.name = mainQuantityInput.dataset.name;
    }
  }

  /**
   * Revierte los inputs principales a formato normal
   */
  revertMainProductToNormal() {
    // Revertir el input id al nombre original y deshabilitar
    if (this.variantIdInput && this.variantIdInput.name === "items[0]id") {
      this.variantIdInput.name = "id";
      this.variantIdInput.disabled = true;
    }

    // Revertir el input quantity al nombre original
    const quantityInput = this.form.querySelector(
      'input[name="items[0]quantity"]',
    );
    if (quantityInput) {
      quantityInput.name = "quantity";
      quantityInput.disabled = true;
    }

    // Limpiar inputs del producto principal
    const mainInputs = [
      ".customization-main-quantity",
      ".customization-main-pid",
      ".customization-main-customized",
      ".customization-main-linked-item",
    ];

    mainInputs.forEach((selector) => {
      const input = this.form.querySelector(selector);
      if (input) {
        input.name = "";
        input.value = "";
      }
    });
  }

  /**
   * Habilita todos los inputs del addon
   * Genera un PID único cuando se activa
   */
  enableAddonInputs(addonIndex) {
    const inputs = this.addonInputsByIndex.get(addonIndex);
    if (!inputs) return;

    // Generar PID si no existe
    let pid = this.pidsByIndex.get(addonIndex);
    if (!pid) {
      pid = this.generatePid();
      this.pidsByIndex.set(addonIndex, pid);
    }

    // Obtener el ID del addon para vincularlo al producto principal
    const addonIdInput = inputs.find((i) =>
      i.dataset.name.includes(`items[${addonIndex}]id`),
    );
    const addonId = addonIdInput ? addonIdInput.value : null;

    // Habilitar inputs del addon
    inputs.forEach((input) => {
      const originalName = input.dataset.name;
      if (originalName) {
        input.name = originalName;

        // Asignar PID al addon
        if (input.classList.contains("customization-addon-pid")) {
          input.value = pid;
        }
      }
    });

    // Sincronizar propiedades del producto principal (items[0]) con el PID y el addon
    const mainPidInput = this.form.querySelector(".customization-main-pid");
    if (mainPidInput && !mainPidInput.name) {
      mainPidInput.name = mainPidInput.dataset.name;
      mainPidInput.value = pid;
    }

    const mainCustomizedInput = this.form.querySelector(
      ".customization-main-customized",
    );
    if (mainCustomizedInput && !mainCustomizedInput.name) {
      mainCustomizedInput.name = mainCustomizedInput.dataset.name;
    }

    const mainLinkedItemInput = this.form.querySelector(
      ".customization-main-linked-item",
    );
    if (mainLinkedItemInput && addonId) {
      if (!mainLinkedItemInput.name) {
        mainLinkedItemInput.name = mainLinkedItemInput.dataset.name;
      }
      mainLinkedItemInput.value = addonId;
    }

    const mainQuantityInput = this.form.querySelector(
      ".customization-main-quantity",
    );
    if (mainQuantityInput && !mainQuantityInput.name) {
      mainQuantityInput.name = mainQuantityInput.dataset.name;
    }
  }

  /**
   * Deshabilita todos los inputs del addon
   */
  disableAddonInputs(addonIndex) {
    const inputs = this.addonInputsByIndex.get(addonIndex);
    if (!inputs) return;

    inputs.forEach((input) => {
      input.name = "";
      // Limpiar valores
      if (input.classList.contains("customization-pid")) {
        input.value = "";
      }
    });

    // Limpiar el texto si existe
    const textInput = this.textInputsByIndex.get(addonIndex);
    if (textInput) {
      textInput.value = "";
      const propertyInput = inputs.find((i) =>
        i.classList.contains("customization-addon-property"),
      );
      if (propertyInput) {
        propertyInput.value = "";
      }
    }
  }

  /**
   * Muestra el input de texto
   */
  showTextInput(addonIndex) {
    const textInput = this.textInputsByIndex.get(addonIndex);
    if (!textInput) return;

    const container = textInput.closest('[id$="_text_container"]');
    if (container) {
      container.classList.remove("hidden");
    }
  }

  /**
   * Oculta el input de texto
   */
  hideTextInput(addonIndex) {
    const textInput = this.textInputsByIndex.get(addonIndex);
    if (!textInput) return;

    const container = textInput.closest('[id$="_text_container"]');
    if (container) {
      container.classList.add("hidden");
    }
  }

  /**
   * Maneja el cambio del input de texto
   * Sincroniza el valor con los inputs hidden de properties
   */
  handleTextInputChange(event) {
    const textInput = event.target;
    const addonIndex = textInput.dataset.addonIndex;
    const value = textInput.value;

    // Encontrar los inputs de property correspondientes
    const inputs = this.addonInputsByIndex.get(addonIndex);
    if (!inputs) return;

    // Sincronizar con items[N][properties][Nombre] (addon)
    const propertyInput = inputs.find((i) =>
      i.classList.contains("customization-addon-property"),
    );
    if (propertyInput) {
      propertyInput.value = value;
    }

    // Sincronizar con items[0][properties][Nombre] (producto principal - usa el mismo nombre)
    const mainTextPropertyInput = inputs.find((i) =>
      i.classList.contains("customization-main-text-property"),
    );
    if (mainTextPropertyInput) {
      mainTextPropertyInput.value = value;
    }
  }

  /**
   * Actualiza todos los parent_id con el variant ID actual
   */
  updateAllParentIds() {
    const currentVariantId = this.variantIdInput.value;

    this.parentIdInputs.forEach((input) => {
      input.value = currentVariantId;
    });
  }
}

// Inicializar cuando el script se carga
window.customizationHandler = new ProductCustomizationFormHandler();
